import React, { useState } from 'react';
import { tarifs } from '../data/zooData';
import PricingCard from '../components/PricingCard';

const BilletsPage: React.FC = () => {
  const [date, setDate] = useState<string>('');
  const [nombreVisiteurs, setNombreVisiteurs] = useState<Record<string, number>>({
    'adulte': 0,
    'enfant': 0,
    'senior': 0
  });
  const [step, setStep] = useState<number>(1);

  // Date minimum = aujourd'hui
  const today = new Date().toISOString().split('T')[0];
  
  // Date maximum = 3 mois à partir d'aujourd'hui
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 3);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const handleIncrement = (type: keyof typeof nombreVisiteurs) => {
    setNombreVisiteurs(prev => ({
      ...prev,
      [type]: prev[type] + 1
    }));
  };

  const handleDecrement = (type: keyof typeof nombreVisiteurs) => {
    if (nombreVisiteurs[type] > 0) {
      setNombreVisiteurs(prev => ({
        ...prev,
        [type]: prev[type] - 1
      }));
    }
  };

  const totalVisiteurs = Object.values(nombreVisiteurs).reduce((a: number, b: number) => a + b, 0);
  
  const handleContinue = () => {
    if (step === 1 && date && totalVisiteurs > 0) {
      setStep(2);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="min-h-screen pb-16">
      {/* Hero section */}
      <div 
        className="h-80 relative flex items-center justify-center"
        style={{ 
          backgroundImage: `url(https://images.unsplash.com/photo-1526667900883-78457802e257?w=1920&auto=format&fit=crop)`,
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Billets & Réservations
          </h1>
          <p className="text-xl text-white max-w-3xl mx-auto">
            Réservez vos billets en ligne et bénéficiez d'un accès prioritaire
          </p>
        </div>
      </div>

      {/* Processus de réservation */}
      <div className="container mx-auto px-4 py-12">
        {/* Étapes */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="flex items-center justify-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
              1
            </div>
            <div className={`h-1 flex-1 ${step >= 2 ? 'bg-green-600' : 'bg-gray-200'}`}></div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
              2
            </div>
            <div className={`h-1 flex-1 ${step >= 3 ? 'bg-green-600' : 'bg-gray-200'}`}></div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
              3
            </div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <div className="text-center flex-1">Date & visiteurs</div>
            <div className="text-center flex-1">Récapitulatif</div>
            <div className="text-center flex-1">Paiement</div>
          </div>
        </div>

        {/* Étape 1: Choix de la date et nombre de visiteurs */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 transform transition-all duration-500 animate-fadeIn">
            <h2 className="text-2xl font-bold text-green-900 mb-6">Choisissez votre date de visite</h2>
            
            <div className="mb-8">
              <label htmlFor="date" className="block text-gray-700 font-medium mb-2">Date de visite</label>
              <input 
                type="date" 
                id="date" 
                min={today}
                max={maxDateStr}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>
            
            <h2 className="text-2xl font-bold text-green-900 mb-6">Nombre de visiteurs</h2>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Adulte</h3>
                  <p className="text-sm text-gray-500">18 ans et plus</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                    onClick={() => handleDecrement('adulte')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="w-6 text-center">{nombreVisiteurs.adulte}</span>
                  <button 
                    className="w-8 h-8 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center"
                    onClick={() => handleIncrement('adulte')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Enfant</h3>
                  <p className="text-sm text-gray-500">3 à 17 ans</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                    onClick={() => handleDecrement('enfant')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="w-6 text-center">{nombreVisiteurs.enfant}</span>
                  <button 
                    className="w-8 h-8 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center"
                    onClick={() => handleIncrement('enfant')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Senior</h3>
                  <p className="text-sm text-gray-500">65 ans et plus</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                    onClick={() => handleDecrement('senior')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="w-6 text-center">{nombreVisiteurs.senior}</span>
                  <button 
                    className="w-8 h-8 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center"
                    onClick={() => handleIncrement('senior')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <button 
              className={`w-full py-3 rounded-md font-bold text-white transition-colors ${
                date && totalVisiteurs > 0 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              onClick={handleContinue}
              disabled={!date || totalVisiteurs === 0}
            >
              Continuer
            </button>
          </div>
        )}
        
        {/* Étape 2: Récapitulatif */}
        {step === 2 && (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 transform transition-all duration-500 animate-fadeIn">
            <h2 className="text-2xl font-bold text-green-900 mb-6">Récapitulatif de votre réservation</h2>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-4">
                <span className="font-medium">Date de visite:</span>
                <span className="text-green-700 font-semibold">{formatDate(date)}</span>
              </div>
              
              <div className="space-y-2 border-b border-gray-200 pb-4 mb-4">
                <h3 className="font-medium mb-2">Visiteurs:</h3>
                {nombreVisiteurs.adulte > 0 && (
                  <div className="flex justify-between items-center">
                    <span>Adulte x {nombreVisiteurs.adulte}</span>
                    <span>{nombreVisiteurs.adulte * 22} €</span>
                  </div>
                )}
                {nombreVisiteurs.enfant > 0 && (
                  <div className="flex justify-between items-center">
                    <span>Enfant x {nombreVisiteurs.enfant}</span>
                    <span>{nombreVisiteurs.enfant * 15} €</span>
                  </div>
                )}
                {nombreVisiteurs.senior > 0 && (
                  <div className="flex justify-between items-center">
                    <span>Senior x {nombreVisiteurs.senior}</span>
                    <span>{nombreVisiteurs.senior * 18} €</span>
                  </div>
                )}
              </div>
              
              <div className="flex justify-between items-center font-bold text-lg">
                <span>Total:</span>
                <span className="text-green-700">
                  {nombreVisiteurs.adulte * 22 + nombreVisiteurs.enfant * 15 + nombreVisiteurs.senior * 18} €
                </span>
              </div>
            </div>
            
            <div className="space-y-4 mb-8">
              <button 
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-md transition-colors"
                onClick={() => setStep(3)}
              >
                Procéder au paiement
              </button>
              
              <button 
                className="w-full bg-white border border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-md transition-colors hover:bg-gray-50"
                onClick={() => setStep(1)}
              >
                Modifier ma réservation
              </button>
            </div>
          </div>
        )}
        
        {/* Étape 3: Confirmation */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 transform transition-all duration-500 animate-fadeIn">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-green-900 my-4">Réservation confirmée !</h2>
              <p className="text-gray-600">
                Votre réservation pour le {formatDate(date)} a été confirmée.<br />
                Un email de confirmation a été envoyé à l'adresse email fournie.
              </p>
            </div>
            
            <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded mb-8">
              <h3 className="font-bold text-green-800 mb-2">Informations importantes</h3>
              <p className="text-gray-700">
                Veuillez vous présenter à l'entrée du zoo 15 minutes avant votre visite muni de votre billet (version papier ou digitale).
              </p>
            </div>
            
            <div className="text-center">
              <button 
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-md transition-colors inline-flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Télécharger mon billet
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Tarification */}
      <div className="container mx-auto px-4 py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl font-bold text-green-900 mb-4">Nos tarifs</h2>
          <p className="text-gray-600">
            Des tarifs adaptés à tous pour une journée inoubliable en famille ou entre amis
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {tarifs.map(tarif => (
            <PricingCard key={tarif.id} tarif={tarif} />
          ))}
        </div>
      </div>
      
      {/* Animation keyframes */}
      <style>
        {
          `
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.5s ease-out;
          }
          `
        }
      </style>
    </div>
  );
};

export default BilletsPage;
